import type { ChangeEvent } from 'react';
import { useCallback, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Loader } from '@mantine/core';
import { IconDeviceMobile, IconMessageCircle } from '@tabler/icons-react';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import { UnstyledButton } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import dt from '~/testing';

import useStyles from './styles';

const CODE_LENGTH = 4;

const dtIds = dt.components.shared.auth.signUpVersionTwo.proveVerificationModal;

function formatPhone(phone: string): string {
  return `+1 ${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
}

interface ProveVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  code: string;
  onCodeChange: (code: string) => void;
  onVerifyCode: () => void;
  onTryAgain: () => void;
  isAwaitingInstantLink?: boolean;
  isLoading?: boolean;
  error?: string;
}

export function ProveVerificationModal({
  isOpen,
  onClose,
  phone,
  code,
  onCodeChange,
  onVerifyCode,
  onTryAgain,
  isAwaitingInstantLink = false,
  isLoading = false,
  error,
}: ProveVerificationModalProps) {
  const { classes, cx, theme } = useStyles();
  const { t } = useTranslation('auth');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
      onCodeChange(digits);
      if (digits.length === CODE_LENGTH) {
        onVerifyCode();
      }
    },
    [onCodeChange, onVerifyCode]
  );

  const lockCursor = useCallback(() => {
    const input = inputRef.current;
    if (input) {
      input.setSelectionRange(code.length, code.length);
    }
  }, [code.length]);

  return (
    <Dialog
      type={{ desktop: 'modal', mobile: 'drawer' }}
      isOpen={isOpen}
      onClose={!isAwaitingInstantLink ? onClose : undefined}
      dataTestId={dtIds.modal}
    >
      <div className={classes.emailVerificationContainer}>
        <div className={classes.iconContainer}>
          <IconMessageCircle size={28} color={theme.colors.dark[9]} />
        </div>

        {isAwaitingInstantLink ? (
          <>
            <div className={classes.headerContainer}>
              <Title variant="subhead-x-large" weight={700} c="gray2.6" align="center">
                {t('signUp.headers.proveVerification.awaitingTitle')}
              </Title>
              <div style={{ maxWidth: 420 }}>
                <Text variant="body-large" c="gray2.4" align="center">
                  {t('signUp.headers.proveVerification.awaitingCopy')}
                </Text>
              </div>
            </div>

            <div className={classes.proveIconContainer}>
              <IconDeviceMobile size={64} color={theme.colors.blue[6]} />
            </div>

            <UnstyledButton
              className={classes.proveResendButton}
              onClick={onTryAgain}
              data-test-id={dtIds.tryAgainLink}
            >
              {t('signUp.headers.proveVerification.resendAction')}
            </UnstyledButton>
          </>
        ) : (
          <>
            <div className={classes.headerContainer}>
              <Title variant="subhead-x-large" weight={700} c="gray2.6" align="center">
                {t('signUp.headers.proveVerification.title')}
              </Title>
              <div style={{ maxWidth: 420 }}>
                <Text variant="body-large" c="gray2.4" align="center">
                  {t('signUp.headers.proveVerification.copy')}
                </Text>
                <Text variant="body-large" c="gray2.6" fw={600} align="center">
                  {formatPhone(phone)}.
                </Text>
              </div>
            </div>

            <div className={classes.pinInputSection}>
              <div
                className={cx(classes.pinInputWrapper, error && classes.pinInputWrapperError)}
                data-test-id={dtIds.codeInput}
              >
                <input
                  ref={inputRef}
                  className={classes.codeInput}
                  type="text"
                  inputMode="numeric"
                  maxLength={CODE_LENGTH}
                  value={code}
                  onChange={handleChange}
                  onSelect={lockCursor}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  autoComplete="one-time-code"
                />
                <div className={classes.proveCodeDisplay} aria-hidden="true">
                  {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                    <span
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className={cx(
                        classes.proveCodeChar,
                        code[index] ? classes.codeCharFilled : classes.codeCharEmpty
                      )}
                    >
                      {code[index] || '-'}
                    </span>
                  ))}
                </div>
                {isLoading && (
                  <div className={classes.pinInputStatus}>
                    <Loader size="sm" color={theme.colors.blue[3]} />
                  </div>
                )}
              </div>
              {error && (
                <Text variant="body-large" c="red.4">
                  {error}
                </Text>
              )}
            </div>

            <UnstyledButton
              className={classes.proveResendButton}
              onClick={onTryAgain}
              data-test-id={dtIds.tryAgainLink}
            >
              {t('signUp.headers.proveVerification.resendAction')}
            </UnstyledButton>
          </>
        )}
      </div>
    </Dialog>
  );
}
